import { useCallback, useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { KpiSectionLayout as SavedKpiSectionLayout } from "@nexus/contracts";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { getUserFacingApiMessage } from "@/lib/api-client";
import { kpiSectionLayoutQueryOptions, userKpiKeys } from "@/lib/queries";
import {
  saveKpiSectionLayout,
  type UserKpiScope,
} from "../api/user-kpis.api";
import type { KpiGroupId } from "../lib/kpi-groups";
import {
  kpiSectionCollapsedKey,
  moveKpiSection,
  normalizeKpiSectionOrder,
  readCollapsedSections,
  removeKpiSection,
  restoreKpiSections,
  toggleKpiSectionCollapsed,
  uniqueKpiSectionIds,
} from "../lib/kpi-section-layout";

function readCollapsed(storageKey: string | null): KpiGroupId[] {
  if (!storageKey) return [];
  try {
    return readCollapsedSections(localStorage.getItem(storageKey));
  } catch {
    return [];
  }
}

function writeCollapsed(storageKey: string | null, collapsed: readonly KpiGroupId[]): void {
  if (!storageKey) return;
  try {
    localStorage.setItem(storageKey, JSON.stringify(collapsed));
  } catch {
    // Private mode or a full quota. The in-memory list still applies this session.
  }
}

const EMPTY_SAVED: SavedKpiSectionLayout = { order: [], removed: [] };

/** Section order and removal follow the user. Collapse stays in this browser. */
export function useKpiSectionLayout(scope: UserKpiScope) {
  const tenantId = useAuth((state) => state.activeTenantId);
  const userId = useAuth((state) => state.user?.sub ?? null);
  const queryClient = useQueryClient();
  const storageKey = tenantId && userId ? kpiSectionCollapsedKey(tenantId, userId) : null;
  const queryKey = userKpiKeys.sections(tenantId ?? "");

  const query = useQuery({
    ...kpiSectionLayoutQueryOptions(scope, tenantId ?? ""),
    enabled: !!tenantId,
  });

  const [collapsed, setCollapsed] = useState<KpiGroupId[]>(() => readCollapsed(storageKey));

  useEffect(() => {
    setCollapsed(readCollapsed(storageKey));
  }, [storageKey]);

  const order = normalizeKpiSectionOrder(uniqueKpiSectionIds(query.data?.order));
  const removed = uniqueKpiSectionIds(query.data?.removed);
  // A click before the saved layout arrives must not write the default order over it.
  const canPersist = Boolean(tenantId) && query.isSuccess;

  const mutation = useMutation({
    mutationFn: (body: SavedKpiSectionLayout) => saveKpiSectionLayout(scope, body),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<SavedKpiSectionLayout>(queryKey);
      queryClient.setQueryData<SavedKpiSectionLayout>(queryKey, body);
      return { previous };
    },
    onError: (error, _body, context) => {
      queryClient.setQueryData(queryKey, context?.previous ?? EMPTY_SAVED);
      toast.error(getUserFacingApiMessage(error));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey });
    },
  });

  const persist = useCallback(
    (next: SavedKpiSectionLayout) => {
      if (!canPersist) return;
      mutation.mutate(next);
    },
    [canPersist, mutation],
  );

  const moveSection = useCallback(
    (id: KpiGroupId, direction: "up" | "down", visibleIds: readonly KpiGroupId[]) => {
      const nextOrder = moveKpiSection(order, id, direction, visibleIds);
      if (nextOrder.every((sectionId, index) => sectionId === order[index])) return;
      persist({ order: nextOrder, removed });
    },
    [order, removed, persist],
  );

  const toggleCollapsed = useCallback(
    (id: KpiGroupId) => {
      const next = toggleKpiSectionCollapsed({ order, collapsed, removed }, id);
      setCollapsed(next.collapsed);
      writeCollapsed(storageKey, next.collapsed);
    },
    [order, collapsed, removed, storageKey],
  );

  const removeSection = useCallback(
    (id: KpiGroupId) => {
      const next = removeKpiSection({ order, collapsed, removed }, id);
      setCollapsed(next.collapsed);
      writeCollapsed(storageKey, next.collapsed);
      if (next.removed.length === removed.length) return;
      persist({ order, removed: next.removed });
    },
    [order, collapsed, removed, storageKey, persist],
  );

  const restoreSections = useCallback(() => {
    const next = restoreKpiSections({ order, collapsed, removed });
    if (next.removed.length === removed.length) return;
    persist({ order, removed: next.removed });
  }, [order, collapsed, removed, persist]);

  return {
    layout: { order, collapsed, removed },
    moveSection,
    toggleCollapsed,
    removeSection,
    restoreSections,
  };
}
